import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getOnboardingDisableToolbar } from '../../../workspace/toolbar/selector/toolbarSelector';
import { ToolbarListType } from '../../../workspace/toolbar/toolbarTypes';

/**
 * Hook that manages a state for requesting a specific toolbar type (eg. TABS/UTILS etc.)
 * and resets it to default after a pointer up event outside the toolbar.
 */
const useDefaultToolbarType = (
    defaultType: ToolbarListType,
): [ToolbarListType, (toolbarType: ToolbarListType) => void] => {
    const [requestedToolbarType, setRequestedToolbarType] = useState<ToolbarListType>(defaultType);
    const onboardingDisableToolbar = useSelector(getOnboardingDisableToolbar);

    // on pointer up, return the requested toolbar type to default
    useEffect(() => {
        const handleUpEvent = (event: PointerEvent | MouseEvent) => {
            if (!event.target) return;

            // if the event was inside a DOM element that contains an attribute of data-toolbar-ignore, do nothing
            if ((event.target as HTMLElement).closest('[data-sheet-ignore-clicks]')) return;

            // Wait a frame to ensure the click event has been processed
            requestAnimationFrame(() => {
                setRequestedToolbarType(defaultType);
            });
        };

        // WEB-12514 - This fixes a strange bug (on iOS only) where two taps would be required
        //  after typing the name of a new board, during onboarding, in order to progress to the next
        //  onboarding step.
        //  Strangely, the presence of this 'pointerup' event listener would stop the mousedown event
        //  from triggering on the canvas - even though this listener doesn't preventDefault or stopPropagation.
        //  Changing it to 'click' during this scenario fixes the issue & doesn't appear to cause any others.
        const eventName = onboardingDisableToolbar ? 'click' : 'pointerup';

        document.addEventListener(eventName, handleUpEvent);

        return () => {
            document.removeEventListener(eventName, handleUpEvent);
        };
    }, [onboardingDisableToolbar]);

    return [requestedToolbarType, setRequestedToolbarType] as const;
};

export default useDefaultToolbarType;
